<script lang="ts" setup>
import { onMounted } from 'vue'
import { useElementClick } from '@/composables/tracks/useElementClick'
import { brand, theme as brandTheme, features, resources } from '@/config/brands'
import { t } from '@/i18n/utils'

withDefaults(
  defineProps<{
    tab?: 'history' | 'home' | 'rewards' | 'member'
  }>(),
  {
    tab: 'home',
  },
)
const { elementClick } = useElementClick()
const aurenovelHomeActiveColor = brand === 'aurenovel' ? '#FFB300' : ''

// 获取 Tab 图标配置（直接使用配置中的完整路径）
const tabIcons = resources.tabIcons

/**
 * 浮动胶囊样式：由品牌配置 theme.components.BasicTabbar 驱动。
 * 未配置的品牌 floating === false，渲染结果与改造前逐字符一致。
 */
const tabbarStyleConfig: any = (brandTheme as any)?.components?.BasicTabbar || {}
const floating = tabbarStyleConfig.floating === true
const floatBg = tabbarStyleConfig.background || 'rgba(255, 255, 255, 0.9)'
const floatShadow = tabbarStyleConfig.shadow || '0 6px 24px rgba(124, 77, 232, 0.15)'
const floatInset = tabbarStyleConfig.inset || '16px'
const floatHeight = tabbarStyleConfig.height || '56px'
const floatRadius = tabbarStyleConfig.radius || '28px'
const activePillBg = tabbarStyleConfig.activePillBg || '#FDEFF6'
const activePillRadius = tabbarStyleConfig.activePillRadius || '22px'

// 预加载所有图标，避免切换时闪烁
onMounted(() => {
  const iconsToPreload = [
    tabIcons.history,
    tabIcons.historyActive,
    tabIcons.home,
    tabIcons.homeActive,
    tabIcons.category,
    tabIcons.categoryActive,
    tabIcons.me,
    tabIcons.meActive,
  ]

  iconsToPreload.forEach((src) => {
    const img = new Image()
    img.src = src
  })
})
</script>

<template>
  <BasicFixedBottom :transparent="floating">
    <div
      class="flex w-full text-n-text-3"
      :class="floating ? 'nm-tabbar-floating' : 'bg-n-bg-pure border-t border-t-solid border-n-bg-pure'"
    >
      <div class="flex-1 text-center py-6px">
        <router-link
          :to="{ name: 'history' }" replace
          :class="{ 'text-n-primary font-bold': tab === 'history' }"
        >
          <div
            :class="floating ? ['nm-tab-inner', { 'nm-tab-inner--active': tab === 'history' }] : ''"
            @click="elementClick('bookshelf')"
          >
            <img
              :src="tab === 'history' ? tabIcons.historyActive : tabIcons.history"
              class="mx-a h-6 w-6"
              alt="Library"
            >
            <p class="mt-1 text-11px" :class="{ 'font-800': floating && tab === 'history' }">
              {{ t('LibraryTab') }}
            </p>
          </div>
        </router-link>
      </div>
      <div class="flex-1 text-center py-6px">
        <router-link
          :to="{ name: 'home' }" replace
          :class="{ 'text-n-primary font-bold': tab === 'home' }"
          :style="tab === 'home' && aurenovelHomeActiveColor ? { color: aurenovelHomeActiveColor } : undefined"
        >
          <div
            :class="floating ? ['nm-tab-inner', { 'nm-tab-inner--active': tab === 'home' }] : ''"
            @click="elementClick('novel')"
          >
            <img
              :src="tab === 'home' ? tabIcons.homeActive : tabIcons.home"
              class="mx-a h-6 w-6"
              alt="Novel"
            >
            <p class="mt-1 text-11px" :class="{ 'font-800': floating && tab === 'home' }">
              {{ t('NovelTab') }}
            </p>
          </div>
        </router-link>
      </div>
      <div v-if="features?.hideRewardsTab !== true" class="flex-1 text-center py-6px">
        <router-link
          :to="{ name: 'welfare' }" replace
          class="flex-1 text-center py-6px"
          :class="{ 'text-n-primary font-bold': tab === 'rewards' }"
        >
          <div
            :class="floating ? ['nm-tab-inner', { 'nm-tab-inner--active': tab === 'rewards' }] : ''"
            @click="elementClick('rewards')"
          >
            <img
              :src="tab === 'rewards' ? tabIcons.categoryActive : tabIcons.category"
              class="mx-a h-6 w-6"
              alt="Rewards"
            >
            <p class="mt-1 text-11px" :class="{ 'font-800': floating && tab === 'rewards' }">
              {{ t('RewardsTab') }}
            </p>
          </div>
        </router-link>
      </div>
      <div class="flex-1 text-center py-6px">
        <router-link
          :to="{ name: 'member' }" replace
          class="flex-1 text-center py-6px"
          :class="{ 'text-n-primary font-bold': tab === 'member' }"
        >
          <div
            :class="floating ? ['nm-tab-inner', { 'nm-tab-inner--active': tab === 'member' }] : ''"
            @click="elementClick('profile')"
          >
            <img
              :src="tab === 'member' ? tabIcons.meActive : tabIcons.me"
              class="mx-a h-6 w-6"
              alt="Profile"
            >
            <p class="mt-1 text-11px" :class="{ 'font-800': floating && tab === 'member' }">
              {{ t('ProfileTab') }}
            </p>
          </div>
        </router-link>
      </div>
    </div>
  </BasicFixedBottom>
</template>

<style scoped>
/* 浮动胶囊容器：仅 theme.components.BasicTabbar.floating = true 的品牌命中 */
.nm-tabbar-floating {
  margin: 0 v-bind(floatInset);
  width: auto;
  height: v-bind(floatHeight);
  align-items: center;
  border-radius: v-bind(floatRadius);
  background: v-bind(floatBg);
  box-shadow: v-bind(floatShadow);
  -webkit-backdrop-filter: blur(10px);
  backdrop-filter: blur(10px);
}

.nm-tab-inner {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4px 14px;
  border-radius: v-bind(activePillRadius);
  transition: background-color 0.2s ease;
}

.nm-tab-inner--active {
  background: v-bind(activePillBg);
}
</style>
